import * as GC from '@grapecity/spread-sheets';
import { IAcquisition } from '@models/acquisition';
import { IDecayEstimate } from '@models/decay/decay-estimate';
import { IDecayCalibration } from '@models/decay/decay-calibration';
import { IFunctionalForm } from '@models/functional-form';
import { IProduct } from '@models/product';
import { setEstimateColours } from '@shared/decay-helpers';
import { round } from '@shared/numbers';
import { borders, colours, decayCoefficientFormatter, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';
import { getFunctionalForm } from '../helpers';

let _acquisitions: IAcquisition[] = [];
let _functionalForms: IFunctionalForm[] = [];

export const addDecayEstimateDetailToSheet = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  product: IProduct,
  calibrations: IDecayCalibration[],
  acquisitions: IAcquisition[],
  functionalForms: IFunctionalForm[]
): [number, number] => {
  let currentCol = 0;

  try {
    _acquisitions = acquisitions;
    _functionalForms = functionalForms;

    rowIndex = populateHeaders(sheet, rowIndex, startCol);
    let estimateSettings = calibrations && calibrations[0] && calibrations[0].DecayEstimateSetting && calibrations[0].DecayEstimateSetting;
    if (estimateSettings) {
      // Sorting Estimates
      estimateSettings = sortEstimates(estimateSettings);

      setEstimateColours(estimateSettings, true);
      estimateSettings.forEach((est, index) => {
        est.Name = `Estimate ${index + 1}`;
        est.DecayResult.DecayCoefficients = est.DecayResult.DecayCoefficients.filter(x => {
          if (x.CoEfficient !== 0 || x.StandardError !== 0 || x.TStat !== 0) {
            return true;
          } else {
            return false;
          }
        });

        [rowIndex, currentCol] = populateEstimateData(
          sheet,
          rowIndex,
          startCol,
          est,
          getFunctionalForm(est.FunctionalForm, functionalForms)
        );
        // rowIndex++;
      });
    } else {
      console.error('There is no latest calibration', { calibrations, product });
    }
  } catch (err) {
    console.error(
      `Error building Decay Estimate Detail sheet`,
      { sheet, product, calibrations, acquisitions, functionalForms, rowIndex },
      err
    );
  }

  return [rowIndex, currentCol];
};

/**
 * This method is responsible for populating all the headers that we see down the left hand side
 * i.e. model, curtailment etc.
 */
function populateHeaders(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, startCol: number): number {
  let takenCols = 0;
  const rowSpan = 1;
  let colSpan = 1;

  // Estimate Name
  sheet.setValue(rowIndex, startCol + takenCols, 'Estimate Name');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Model
  sheet.setValue(rowIndex, startCol + takenCols, 'Model');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Start Period
  sheet.setValue(rowIndex, startCol + takenCols, 'Start Period');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // End Period
  sheet.setValue(rowIndex, startCol + takenCols, 'End Period');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Curtailment
  sheet.setValue(rowIndex, startCol + takenCols, 'Curtailment');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Coefficient
  colSpan = 2;
  sheet.setValue(rowIndex, startCol + takenCols, 'Coefficients');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // S.E
  sheet.setValue(rowIndex, startCol + takenCols, 'S.E');
  takenCols++;

  // T-STAT
  sheet.setValue(rowIndex, startCol + takenCols, 'T-STAT');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  // R^2 & S.E.
  sheet.setValue(rowIndex, startCol + takenCols, 'R^2 & S.E.');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  // F-STAT & D.F.
  sheet.setValue(rowIndex, startCol + takenCols, 'F-STAT & D.F.');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Styling
  const headerArea = sheet.getRange(rowIndex, startCol, rowSpan, takenCols);
  headerArea
    .backColor(colours.LIGHT_GREY)
    .font(`bold ${FONT_SIZE}pt ${FONT_NAME}`)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center);
  headerArea.setBorder(borders.BLACK_MEDIUM, { all: true });

  rowIndex++;

  return rowIndex;
}

function populateEstimateData(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  estimate: IDecayEstimate,
  functionalFormName: string
): [number, number] {
  const numDecimals = 4;
  let colSpan = 1;
  const rowSpan = 3;
  let takenCols = 0;
  let takenRows = 0;

  // Estimate Name
  sheet.getCell(rowIndex, startCol + takenCols).text(estimate.Name);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Model
  sheet.getCell(rowIndex, startCol + takenCols).text(functionalFormName);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Start Period
  sheet.getCell(rowIndex, startCol + takenCols).text(`${estimate.StartObservation}`);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // End Period
  sheet.getCell(rowIndex, startCol + takenCols).text(`${estimate.EndObservation}`);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Curtailment
  colSpan = 1;
  sheet.getCell(rowIndex, startCol + takenCols).text(`${estimate.Curtailment}`);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Coefficient
  colSpan = 2;
  try {
    if (estimate.DecayResult.DecayCoefficients.length > 0) {
      estimate.DecayResult.DecayCoefficients.forEach((coeff, i) => {
        const coefficientName = `β${i}:`;
        const coefficient = estimate.DecayResult.DecayCoefficients[i].CoEfficient;
        sheet.getCell(rowIndex + i, startCol + takenCols).text(coefficientName);
        sheet
          .getCell(rowIndex + i, startCol + takenCols + 1)
          .value(coefficient)
          .formatter(decayCoefficientFormatter);
      });
    }
  } catch (err) {
    console.error(`Unable to get Decay Coefficients from estimate`, estimate);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    sheet.getCell(rowIndex, startCol + takenCols).text('N/A');
  }
  takenCols += colSpan;

  // S.E.
  estimate.DecayResult.DecayCoefficients.forEach((coef, i) => {
    const coeff = round(coef.StandardError, numDecimals);
    if (coeff) {
      sheet.getCell(rowIndex + i, startCol + takenCols).value(coeff);
    }
  });

  takenCols++;

  // T-STAT
  estimate.DecayResult.DecayCoefficients.forEach((coef, i) => {
    const coeff = round(coef.TStat, numDecimals);
    if (coeff) {
      sheet
        .getCell(rowIndex + i, startCol + takenCols)
        .value(coeff)
        .formatter(decayCoefficientFormatter);
    }
  });
  takenCols++;

  // R^2 & S.E.
  sheet
    .getCell(rowIndex, startCol + takenCols)
    .value(estimate.DecayResult.RSquared)
    .formatter(decayCoefficientFormatter);
  sheet
    .getCell(rowIndex + 1, startCol + takenCols)
    .value(estimate.DecayResult.StandardError)
    .formatter(decayCoefficientFormatter);
  takenCols++;

  // F-STAT & DF
  sheet.setArray(rowIndex, startCol + takenCols, ['F-Stat:', 'D.F.:']);
  sheet
    .getCell(rowIndex, startCol + takenCols + 1)
    .value(estimate.DecayResult.FStat)
    .formatter(decayCoefficientFormatter);
  sheet
    .getCell(rowIndex + 1, startCol + takenCols + 1)
    .value(estimate.DecayResult.DegreeFreedom)
    .formatter(decayCoefficientFormatter);
  takenCols += colSpan;

  takenRows += rowSpan;

  // Styling
  const estimateArea = sheet.getRange(rowIndex, startCol, takenRows, takenCols);

  estimateArea.backColor(estimate.Colour).wordWrap(true);
  estimateArea.hAlign(GC.Spread.Sheets.HorizontalAlign.center);
  estimateArea.vAlign(GC.Spread.Sheets.VerticalAlign.center);
  estimateArea.setBorder(borders.BLACK_MEDIUM, { all: true });
  estimateArea.setBorder(borders.GREY_NORMAl, { inside: true });

  rowIndex += takenRows;

  return [rowIndex, startCol + takenCols];
}

export const sortEstimates = (estimates: IDecayEstimate[]) => {
  const estimateInd = estimates.findIndex(est => est.IsChosen);
  if (estimateInd >= 0) {
    const chosenEstimate = estimates.splice(estimateInd, 1);
    estimates.unshift(chosenEstimate[0]);
    return estimates;
  } else {
    return estimates;
  }
};
